import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "../../shared/api/authFetch";
import { admin_apiConfig } from "../../shared/config/config";
import { useAuth } from "../../app/providers/AuthProvider";

export async function teamRequest(path = "", options = {}) {
  const response = await authFetch(
    `${admin_apiConfig.baseURL}/api/organization/team${path}`,
    options,
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(
      typeof body.detail === "string"
        ? body.detail
        : "Team access could not be loaded. Try again.",
    );
  return body;
}

export function useProcessTeam(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["processTeam", user?.id],
    queryFn: () => teamRequest(),
    enabled: enabled && Boolean(user?.id),
    staleTime: 15_000,
    refetchInterval: (query) => query.state.data?.invitations?.some((i) => i.status === "pending" && ["pending", "submitted", "processed", "deferred", "unknown"].includes(i.delivery_status)) ? 30_000 : false,
  });
}

export function useTeamMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body, method = "POST" }) =>
      teamRequest(path, { method, body: JSON.stringify(body || {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["processTeam"] });
      queryClient.invalidateQueries({
        queryKey: ["userProfilesAndInvitations"],
      });
      queryClient.invalidateQueries({ queryKey: ["currentUserRole"] });
    },
  });
}

export function processActivityUrl(activity) {
  return `/app/build?${new URLSearchParams({ process: activity.process_id, perspective: activity.perspective, step: activity.step_key })}`;
}

/** A role's responsibilities, people and access in the Org Chart's Roles view. */
export function orgChartRoleUrl(roleId) {
  return `/app/org-chart?${new URLSearchParams({ view: "roles", role: roleId })}`;
}

/** Roles with work in the process map and nobody active in them. */
export function rolesNeedingSomeone(team = {}) {
  return (team.roles || []).filter(
    (role) => role.activities?.length && !roleStaffing(role, team.invitations).active.length,
  );
}

export function roleStaffing(role, invitations = []) {
  const active = (role?.members || []).filter(
    (member) => member.is_active && member.membership_active !== false,
  );
  const pending = invitations.filter(
    (invite) =>
      String(invite.role_id) === String(role?.id) &&
      invite.status === "pending" &&
      ["submitted", "processed", "deferred", "delivered"].includes(invite.delivery_status),
  );
  return {
    active,
    pending,
    label: active.length
      ? `${active.length} assigned`
      : pending.length
        ? "Invitation pending"
        : "Nobody assigned",
  };
}
